import * as React from "react";
import { useEffect, useMemo } from "react";
import styled from "styled-components";
import {
  AnimationQuestion,
  ImageQuestion,
  MultiselectQuestion,
  SelectQuestion,
  SpecialSelectQuestion,
  TextQuestion,
} from "../../components/PageSegments/onboarding/";
import { AppButton } from "../../components/ui";
import {
  ContentCategory,
  DataT,
  QUESTION_TYPES,
  thumbsData,
  globalThumbQuestions,
  appOnboardingQuestions,
  topicToQuestionsMapping,
} from "../../content/static/onboardingQuestions";
import { useCounter } from "../../hooks/useCounter";
import { BasicLayout } from "../../layouts/BasicLayout";
import {
  onboarding_questions_final_report,
  onboarding_questions_start,
  setMixpanelProperties,
} from "../../services/mixpanel/mixpanel";
import { preloadImage, storeUTMCookies, storeUTMDataToLocalStorage } from "../../util/misc";
import theme from "../../util/theme";
import { mobileSize } from "../../util/variables";
import { useOnboardingContext } from "../../components/PageSegments/onboarding/OnboardingContext";
import { navigate } from "gatsby";
import { fbq } from "../../services/pixel";
import {
  web_acq_question_view,
  web_acq_submit_answer,
} from "../../services/mixpanel/acquisition-events";
import ThumbQuestion from "../../components/PageSegments/onboarding/questions/ThumbQuestion";
import { getUserInfo } from "../../services/firebase";
import CheckboxMultiselectQuestion from "../../components/PageSegments/onboarding/questions/CheckboxMultiselectQuestion";
import { WEB_PAID_TRIAL_FLOW } from "../get-started";
import ScaleQuestion from "../../components/PageSegments/onboarding/questions/ScaleQuestion";
import YesNoQuestion from "../../components/PageSegments/onboarding/questions/YesNoQuestion";
import { WEB_PLANS_FLOW } from "../signup";
import { buddhismOnboardingQuestions } from "../../content/static/onboardingQuestions";

export const LOCAL_STORAGE_VARIANT_KEY = "onboarding_variant";

const DEFAULT_THUMB_QUESTIONS = [
  thumbsData[ContentCategory.Philosophy][0],
  thumbsData[ContentCategory.Psychology][0],
  thumbsData[ContentCategory.ScienceAndTech][0],
  thumbsData[ContentCategory.Economics][0],
];

const DEFAULT_VARIANT_QUESTIONS = [
  ...topicToQuestionsMapping["Personal Development"],
  ...topicToQuestionsMapping.Philosophy,
];

const LEARN_BUDDHISM_FLOW = "buddhism_topic";

type OnboardingVariantT = "control" | "variant";

const prepareOnboardingQuestions = (
  onboardingVariant: OnboardingVariantT,
  onboardingLandingPage: string,
) => {
  // Check for Buddhism topic-specific onboarding
  if (
    onboardingLandingPage === LEARN_BUDDHISM_FLOW ||
    onboardingLandingPage === "buddhism_topic_test"
  ) {
    return [...buddhismOnboardingQuestions];
  }

  // Existing logic unchanged
  let initialQuestions;
  switch (onboardingVariant) {
    // Currently not running any Ax/B tests
    // case "variant": {
    //   initialQuestions = [...questions_202503_QuizSkillsOnboarding];
    //   const TwoMinuteQuizIndex = initialQuestions.findIndex(q => q.id === "2MinuteQuiz");
    //   initialQuestions.splice(TwoMinuteQuizIndex + 1, 0, ...DEFAULT_VARIANT_QUESTIONS);
    //   break;
    // }

    default:
      initialQuestions = [...appOnboardingQuestions];
      initialQuestions.splice(initialQuestions.length - 1, 0, ...DEFAULT_THUMB_QUESTIONS);
  }

  return initialQuestions;
};

export default ({ location }) => {
  const { counter, increment } = useCounter();
  const {
    onboardingLandingPage,
    onboardingAnswers,
    updateOnboardingAnswer,
    saveOnboardingDataToLocalStorage,
  } = useOnboardingContext();
  const { search, state } = location;
  const onboardingVariant: OnboardingVariantT =
    typeof window === "undefined"
      ? "control"
      : (localStorage.getItem(LOCAL_STORAGE_VARIANT_KEY) as OnboardingVariantT);
  const preparedOnboardingQuestions = useMemo(
    () => prepareOnboardingQuestions(onboardingVariant, onboardingLandingPage),
    [onboardingVariant, onboardingLandingPage, state],
  );
  const [questionSet, setQuestionSet] = React.useState(preparedOnboardingQuestions);
  const [questionData, setQuestionData] = React.useState(preparedOnboardingQuestions[0]);
  const [ABQuestionsAdded, setABQuestionsAdded] = React.useState(false);
  const params = new URLSearchParams(search);
  const flowParam = params.get("flow");
  const fromGift = params.get("fromGift");
  const offerParam = params.get("offer");
  const user = getUserInfo();

  // handle query params
  useEffect(() => {
    onboarding_questions_start();
    if (flowParam) {
      localStorage.setItem("flow", flowParam);
    }

    if (search) {
      storeUTMCookies(search);
    }
  }, [search, flowParam]);

  const shouldDisableButton = () => {
    if (
      questionData.type !== QUESTION_TYPES.MultiSelect &&
      questionData.type !== QUESTION_TYPES.Select &&
      questionData.type !== QUESTION_TYPES.SpecialSelect &&
      questionData.type !== QUESTION_TYPES.CheckboxMultiSelect &&
      questionData.type !== QUESTION_TYPES.CustomSelect
    ) {
      return false;
    }

    const answerKey = Object.keys(onboardingAnswers).find(key => key === questionData.key);
    const answerAdded = answerKey ? !!onboardingAnswers[answerKey] : false;

    return !answerAdded;
  };

  const shouldShowQuestion = (question: DataT, answers: any) => {
    if (!question.showWhen) return true;

    for (const [questionKey, requiredAnswer] of Object.entries(question.showWhen)) {
      if (answers[questionKey] !== requiredAnswer) {
        return false;
      }
    }
    return true;
  };

  const nextQuestion = async () => {
    let answerChoice;
    if (questionData.hasOwnProperty("key")) {
      answerChoice = onboardingAnswers[questionData.key];
    }

    if (questionData.id === "ageSurvey" || questionData.id === "genderSurvey") {
      const key =
        questionData.id === "ageSurvey"
          ? "onboarding_prompt_ageSurvey"
          : "onboarding_prompt_genderSurvey";
      setMixpanelProperties({
        [key]: answerChoice,
      });
    }

    const VIEW_QUESTION_TYPES = [
      QUESTION_TYPES.Image,
      QUESTION_TYPES.Text,
      QUESTION_TYPES.Animation,
    ];
    if (VIEW_QUESTION_TYPES.includes(questionData.type)) {
      updateOnboardingAnswer(questionData.id, "viewed");
    }

    if (
      questionData.type !== QUESTION_TYPES.YesNo &&
      questionData.type !== QUESTION_TYPES.Scale &&
      questionData.type !== QUESTION_TYPES.Thumb
    ) {
      // web_acq_submit_answer is sent directly in the thumb, yesno, and scale question components. This is not an ideal solution
      web_acq_submit_answer(questionData.id, answerChoice);
    }

    // 202503 quiz skills onboarding
    if (questionData.id === "whichTopicsDoYouWant_topics" && onboardingVariant === "variant") {
      const topics = answerChoice.split(", ");
      const topicQuestions = Object.entries(topicToQuestionsMapping)
        .filter(([topic, _]) => topics.includes(topic))
        .reduce((allTopics, [_, value]) => {
          const uniqueQuestions = value.filter(
            question =>
              !allTopics.some(
                existingQuestion => existingQuestion.statement === question.statement,
              ),
          );
          return [...allTopics, ...uniqueQuestions];
        }, [] as any[]);

      const newQuestionSet = questionSet.slice();
      newQuestionSet.splice(7, DEFAULT_VARIANT_QUESTIONS.length, ...topicQuestions);
      setQuestionSet(newQuestionSet);
      // setQuestionData(newQuestionSet[counter + 1]);
      // increment();
      // return;
    }

    // Find the next question that should be shown
    const remainingQuestions = questionSet.slice(counter + 1);
    const nextValidQuestionIndex = remainingQuestions.findIndex(question =>
      shouldShowQuestion(question, onboardingAnswers),
    );

    if (nextValidQuestionIndex === -1) {
      // No more valid questions found - handle end of questions
      saveOnboardingDataToLocalStorage();
      if (!localStorage.getItem("utmData")) {
        storeUTMDataToLocalStorage();
      }

      onboarding_questions_final_report();
      fbq("trackCustom", "View_trial_prompt");

      if (fromGift) {
        if (!user?.email) {
          alert("Please login again to continue");
          return;
        }
        navigate("/gift/redeem");
        return;
      } else {
        let path;
        switch (onboardingLandingPage) {
          case WEB_PLANS_FLOW:
          case WEB_PAID_TRIAL_FLOW:
            // Conditional redirect based on domain
            if (
              typeof window !== "undefined" &&
              (window.location.hostname.includes("learnwithimprint.com") ||
                window.location.hostname.includes("luc-4601-sso-by-domain"))
            ) {
              path = "/create-account/email";
            } else {
              path = "/create-account";
            }
            break;
          case LEARN_BUDDHISM_FLOW:
          case "buddhism_topic_test":
            if (
              (typeof window !== "undefined" &&
                window.location.hostname.includes("learnwithimprint.com")) ||
              onboardingLandingPage === "buddhism_topic_test"
            ) {
              path = "/create-account/email";
            } else {
              path = "/create-account";
            }
            break;
          default:
            path =
              flowParam === "polywise-ff"
                ? "/subscription/create-account/polywise-ff"
                : `/special-offer/join/${offerParam ? `?offer=${offerParam}` : ""}`;
        }
        navigate(path);
        return;
      }
    }

    // Calculate the actual index in the full question set
    const nextCounter = counter + 1 + nextValidQuestionIndex;

    // Set the next valid question
    setQuestionData(questionSet[nextCounter]);
    const incrementsNeeded = nextCounter - counter;
    for (let i = 0; i < incrementsNeeded; i++) {
      increment();
    }
  };

  const renderQuestion = () => {
    switch (questionData.type) {
      case QUESTION_TYPES.Animation:
        return (
          <AnimationQuestion
            key={questionData.id}
            title={questionData.title}
            subtitle={questionData.subtitle}
            asset={questionData.asset}
            hardcode={questionData.hardcode}
          />
        );
      case QUESTION_TYPES.Image:
        return (
          <ImageQuestion
            key={questionData.id}
            src={questionData.asset}
            alt={questionData.asset}
            title={questionData.title}
          />
        );
      case QUESTION_TYPES.MultiSelect:
        return (
          <MultiselectQuestion
            key={questionData.id}
            id={questionData.id}
            title={questionData.title}
            subtitle={questionData.subtitle}
            options={questionData.options}
            answer_key={questionData.key}
          />
        );
      case QUESTION_TYPES.Select:
        return (
          <SelectQuestion
            key={questionData.id}
            id={questionData.id}
            title={questionData.title}
            subtitle={questionData.subtitle}
            options={questionData.options}
            answer_key={questionData.key}
          />
        );
      case QUESTION_TYPES.CheckboxMultiSelect:
        return (
          <CheckboxMultiselectQuestion
            key={questionData.id}
            id={questionData.id}
            title={questionData.title}
            subtitle={questionData.subtitle}
            options={questionData.options}
            answer_key={questionData.key}
            max_choices={questionData.max_choices}
          />
        );
      case QUESTION_TYPES.SpecialSelect:
        return (
          <SpecialSelectQuestion
            key={questionData.id}
            id={questionData.id}
            answer_key={questionData.key}
            variant={questionData.variant}
          />
        );
      case QUESTION_TYPES.Text:
        return (
          <TextQuestion
            key={questionData.id}
            title={questionData.title}
            color={questionData.color}
            textAlign={questionData.textAlign}
          />
        );
      case QUESTION_TYPES.Thumb:
        return (
          <ThumbQuestion
            data={questionData}
            nextQuestion={nextQuestion}
            questionNumber={questionData.questionNumber ?? 0}
          />
        );
      case QUESTION_TYPES.Scale:
        return (
          <ScaleQuestion
            id={questionData.id}
            key={questionData.id}
            data={questionData}
            nextQuestion={nextQuestion}
          />
        );
      case QUESTION_TYPES.YesNo:
        return (
          <YesNoQuestion
            id={questionData.id}
            key={questionData.id}
            data={questionData}
            nextQuestion={nextQuestion}
          />
        );
      case QUESTION_TYPES.CustomSelect:
        return questionData.component;
      case QUESTION_TYPES.Custom:
        return questionData.component;
      default:
        return <div>question not recognized</div>;
    }
  };

  useEffect(() => {
    function preloadFutureImage(data: DataT) {
      switch (data.type) {
        case QUESTION_TYPES.Animation:
        case QUESTION_TYPES.Image:
        case QUESTION_TYPES.Thumb:
          preloadImage(data.asset);
          break;
        case QUESTION_TYPES.MultiSelect || QUESTION_TYPES.Select:
          data.options.forEach(opt => {
            if (opt.image) {
              preloadImage(opt.image);
            }
          });
          break;
        default:
          break;
      }
    }

    if (counter + 1 < questionSet.length) {
      preloadFutureImage(questionSet[counter + 1]);
    }

    if (counter + 2 < questionSet.length) {
      preloadFutureImage(questionSet[counter + 2]);
    }
  }, [counter]);

  useEffect(() => {
    if (!questionData) return;
    if (questionData.id === "quoteRevamped_2" && onboardingVariant !== "variant") {
      // hardcoded arbitrary question to send pixel event
      fbq("track", "ViewContent");

      // add 3 additional questions based on topics + 1 static
      if (!ABQuestionsAdded && !!onboardingAnswers["whichTopicsDoYouWant_3"]) {
        const getAndSortTopics = () => {
          const inputtedTopics = onboardingAnswers["whichTopicsDoYouWant_3"].split(", ");
          // always guarantee 3 topics
          const topics = [...inputtedTopics, ...inputtedTopics, ...inputtedTopics].slice(0, 3);

          const categoryPriority = {
            [ContentCategory.Philosophy]: 0,
            [ContentCategory.Psychology]: 1,
            [ContentCategory.ScienceAndTech]: 2,
            [ContentCategory.Economics]: 3,
            [ContentCategory.Productivity]: 4,
            [ContentCategory.Leadership]: 5,
            [ContentCategory.SelfHelp]: 6,
            [ContentCategory.History]: 7,
            [ContentCategory.HealthAndWellness]: 8,
            [ContentCategory.Business]: 9,
          };

          topics.sort((a, b) => {
            const indexA = categoryPriority[a as ContentCategory];
            const indexB = categoryPriority[b as ContentCategory];
            return indexA - indexB;
          });

          return topics;
        };

        const generateThumbQuestions = (topics: ContentCategory[]) => {
          return topics.reduce((curAdditionalQuestions, topic, index) => {
            const questionsData = thumbsData[topic];
            const questionToAdd = questionsData.find(q => {
              return !curAdditionalQuestions.some(aq => aq.id === q.id);
            });
            return questionToAdd
              ? curAdditionalQuestions.concat({
                  ...questionToAdd,
                  questionNumber: index + 1 + globalThumbQuestions.length,
                })
              : curAdditionalQuestions;
          }, globalThumbQuestions);
        };

        const topics = getAndSortTopics();
        const additionalQuestions = generateThumbQuestions(topics);
        const newQuestionSet = questionSet.slice();
        newQuestionSet.splice(-5, DEFAULT_THUMB_QUESTIONS.length, ...additionalQuestions);

        setQuestionSet(newQuestionSet);
        setABQuestionsAdded(true);
      }
    }
  }, [questionData, ABQuestionsAdded, onboardingAnswers, questionSet, onboardingVariant]);

  useEffect(() => {
    if (!questionData) {
      return;
    }

    web_acq_question_view(questionData.id);
  }, [questionData]);

  return (
    <BasicLayout themeColor={theme.WHITE_COLOR}>
      <Wrapper
        background={
          questionData.hasOwnProperty("background") ? questionData.background : theme.WHITE_COLOR
        }
      >
        <Content>
          <ProgressBar
            childrenLength={questionSet.length}
            currentIndex={counter}
            color={onboardingLandingPage === LEARN_BUDDHISM_FLOW ? theme.PRIMARY_COLOR : undefined}
          />
          {renderQuestion()}
          <Spacer />
          {counter < questionSet.length &&
            questionData.type !== QUESTION_TYPES.Thumb &&
            questionData.type !== QUESTION_TYPES.YesNo &&
            questionData.type !== QUESTION_TYPES.Scale && (
              <ContinueButton
                type="button"
                background={
                  questionData.hasOwnProperty("background") &&
                  questionData.background !== theme.WHITE_COLOR
                    ? "#292032"
                    : undefined
                }
                disabled={shouldDisableButton()}
                onClick={nextQuestion}
              >
                Continue
              </ContinueButton>
            )}
        </Content>
      </Wrapper>
    </BasicLayout>
  );
};

const Wrapper = styled.div<{ background: string }>`
  width: 100%;
  height: 100dvh;
  background-color: ${props => props.background};
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  height: 100dvh;
  max-width: 800px;
  box-sizing: border-box;
  padding: 48px;
  margin: 0 auto;
  @media ${mobileSize} {
    padding: 36px;
    /* justify-content: space-between; */
  }
`;

interface FillerProps {
  childrenLength: number;
  currentIndex: number;
  color?: string;
}

const ProgressBar = styled.div<FillerProps>`
  position: relative;
  height: 8px;
  min-height: 8px;
  width: 334px;
  margin: 0px auto;
  margin-bottom: 48px;
  border-radius: 50px;
  background-color: #edeeef;
  z-index: 100;

  @media ${mobileSize} {
    margin: 0 auto;
    width: 100%;
    max-width: 240px;
    margin-bottom: 40px;
  }

  ::before {
    content: "";
    display: block;
    position: absolute;
    left: 0;
    background-color: ${props => props.color || theme.GREEN_ACCENT};
    height: inherit;
    border-radius: inherit;
    transition: width 0.2s ease-out;
    width: ${props => `${(props.currentIndex / (props.childrenLength - 1)) * 100}%`};
  }
`;

const ContinueButton = styled(AppButton as any)`
  margin-top: auto;
  height: 48px;
  @media ${mobileSize} {
    width: 100%;
  }
`;

const Spacer = styled.div`
  padding: 12px; // continue_button_height / 2
  @media ${mobileSize} {
    /* padding: 48px; */
  }
`;
